import React, { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItemButton,
  ListItemText,
  Box,
  LinearProgress,
  Button,
} from '@mui/material';
import { ArrowCircleLeftOutlined, ArrowCircleRightOutlined, Autorenew } from '@mui/icons-material';
import {
  ISectionStatistics,
  ISubCategoryStatistics,
  ITopSectionStatistics,
} from '@shared/types';
import SectionStatistics from '../core/SectionStatistics';
import { LoaderComponent } from '../core/LoaderComponent';
import { observer } from 'mobx-react-lite';
import { useStoreByName } from '../../../hooks/use-stores.hooks';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { numberToGematria } from '../../../utils/text-hebrew.utils';
import { getEnglishBookName } from '../../../constants/books.constants';

interface TanachAccordionProps {
  bookName: string;
  levelOneData: ISubCategoryStatistics;
  expanded: boolean;
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
  isTeacher: boolean;
  isOnlyAccordion?: boolean;
}

const TanachAccordion: React.FC<TanachAccordionProps> = ({
  bookName,
  levelOneData,
  expanded,
  onChange,
  isTeacher,
}) => {
  const booksStore = useStoreByName('booksStore');

  // State management for Tanach structure (3 levels)
  const [selectedLevelTwo, setSelectedLevelTwo] = useState<ITopSectionStatistics | null>(
    levelOneData?.top_section_array?.length > 0 ? levelOneData.top_section_array[0] : null
  );

  const [selectedLevelThree, setSelectedLevelThree] = useState<ISectionStatistics | null>(null);

  // Loading state for handleReadAgain
  const [isLoadingReadAgain, setIsLoadingReadAgain] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Progress calculation for Tanach
  const progress = ((levelOneData?.read_count || 0) / (levelOneData?.section_count || 1)) * 100;

  // Get display names for Tanach
  const getDisplayName = (levelIdx: number) => {
    const displayNames = ['פרקים', 'פסוקים', 'פסוק'];
    return displayNames[levelIdx] || '';
  };

  // Get count label for Tanach
  const getCountLabel = () => 'manyVerses';

  // Get plural label for Tanach
  const getPluralLabel = () => 'verses';

  // Reset selections when switching books
  useEffect(() => {
    setSelectedLevelTwo(levelOneData?.top_section_array?.length > 0 ? levelOneData.top_section_array[0] : null);
    setSelectedLevelThree(
      levelOneData?.top_section_array?.length > 0 && levelOneData.top_section_array[0]?.section_array?.length > 0
        ? levelOneData.top_section_array[0].section_array[0]
        : null
    );
  }, [levelOneData]);

  // Update level three when level two changes
  useEffect(() => {
    const regularSection = selectedLevelTwo;
    setSelectedLevelThree(
      regularSection?.section_array?.length && regularSection.section_array.length > 0 ? regularSection.section_array[0] : null
    );
  }, [selectedLevelTwo]);

  const handleReadAgain = async () => {
    setIsLoadingReadAgain(true);
    
    // Start timer for minimum loading time
    const startTime = Date.now();
    
    try {

      // בדיקה אם יש מידע היררכי
      if (!selectedLevelThree?.hierarchy_info) {
        console.error('❌ Tanach - Missing hierarchy_info in selectedLevelThree');
        setIsLoadingReadAgain(false);
        return;
      }

      const hierarchyInfo = selectedLevelThree.hierarchy_info;




      const result = await booksStore.setReadingContext({
        bookName: getEnglishBookName(bookName), // נשאיר את זה כי זה יכול להיות תורה/נביאים/כתובים
        levelOneLabel: hierarchyInfo.category_name || '', // קטגוריה (למשל "נביאים ראשונים")
        levelTwoLabel: hierarchyInfo.sub_category_name || '', // ספר (למשל "יהושע")
        levelThreeLabel: hierarchyInfo.top_section_name?.toString() || '', // פרק (למשל "פרק א")
        levelFourLabel: hierarchyInfo.section_name?.toString() || '', // פסוק (למשל "פסוק א")
      });

      if (!result) {
        setIsLoadingReadAgain(false);
        return;
      }


      // Ensure minimum loading time of 300ms
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < 300) {
        await new Promise(resolve => setTimeout(resolve, 300 - elapsedTime));
      }

      // Determine if we're in student area or teacher's personal area
      const isStudentArea = location.pathname === '/student-area';

      if (isStudentArea) {
        // Navigate with state to indicate we came from student area
        navigate('/reading', {
          state: {
            fromStudentArea: true,
            studentData: location.state?.studentData,
            studentName: location.state?.studentName,
            studentId: location.state?.studentId,
          },
        });
      } else {
        // Navigate with state to indicate we came from personal area
        navigate('/reading', { state: { fromPersonalArea: true } });
      }
    } catch (error) {
      console.error('❌ Tanach - Error in handleReadAgain:', error);
      setIsLoadingReadAgain(false);
    }
  };

  // Show loader when loading
  if (isLoadingReadAgain) {
    return <LoaderComponent />;
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      slotProps={{ transition: { timeout: { enter: 600, exit: 800 } } }}
    >
      <AccordionSummary
        expandIcon={i18n.language === 'en' ? <ArrowCircleRightOutlined /> : <ArrowCircleLeftOutlined />}
      >
        <div className="headers">
          <h3 style={{ color: 'primary' }}>
            {levelOneData?.sub_category_name_he || 'קטע קריאה'}
          </h3>
          <h5>
            {`${levelOneData?.top_section_count || 0} ${t('chapters')} | ${
              levelOneData?.section_count || 0
            } ${t(getPluralLabel())}`}
          </h5>
        </div>
        <div className="progress">
          <label>
            {`${t(getCountLabel())}: ${levelOneData?.read_count || 0} / ${
              levelOneData?.section_count || 0
            }`}
          </label>
          <div style={{ transform: 'scaleX(-1)' }}>
            <LinearProgress variant="determinate" value={progress} />
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <div className="container">
          {/* First column - Top Sections (פרקים) */}
          {levelOneData?.top_section_array?.length > 0 && (
            <div className="column">
              <h5 style={{ paddingInline: '6px' }}>{getDisplayName(0)}</h5>
              <List>
                {levelOneData.top_section_array.map((levelTwo, index) => (
                  <ListItemButton
                    key={levelTwo.top_section_id}
                    selected={selectedLevelTwo?.top_section_id === levelTwo.top_section_id}
                    onClick={() => setSelectedLevelTwo(levelTwo)}
                  >
                    <ListItemText
                      primary={<p>{numberToGematria(levelTwo.top_section_name_he ?? index + 1)}</p>}
                    />
                  </ListItemButton>
                ))}
              </List>
            </div>
          )}

          {/* Second column - Shinun Sections (פסוקים) */}
          {selectedLevelTwo?.section_array?.length && selectedLevelTwo.section_array.length > 0 && (
            <div className="column">
              <h5 style={{ paddingInline: '6px' }}>{getDisplayName(1)}</h5>
              <List>
                {selectedLevelTwo.section_array.map((levelThree, index) => (
                  <ListItemButton
                    key={`${levelThree.section_name}-${index}`}
                    selected={selectedLevelThree?.section_name === levelThree.section_name}
                    onClick={() => setSelectedLevelThree(levelThree)}
                  >
                    <ListItemText primary={<p>{numberToGematria(levelThree.section_name)}</p>} />
                  </ListItemButton>
                ))}
              </List>
            </div>
          )}

          {/* Third column - Statistics */}
          <div className="column">
            {selectedLevelThree && (
              <Box>
                <div className="details">
                  <div className="head-line">
                    <h3>
                      {(() => {
                        if (!selectedLevelThree) return '';
                        const sectionName = String(selectedLevelThree.section_name);
                        return sectionName.includes('פסוק') ? sectionName : `פסוק ${sectionName}`;
                      })()}
                    </h3>
                    <h4>
                      {`${levelOneData?.sub_category_name_he || ''} | ${(() => {
                        const regularSection = selectedLevelTwo;
                        const sectionName = regularSection?.top_section_name_he ?? regularSection?.top_section_name;
                        if (sectionName === undefined || sectionName === null) {
                          return '';
                        }
                        const sectionNumber = Number(sectionName);
                        if (isNaN(sectionNumber)) {
                          return sectionName;
                        }
                        return `${t('chapter')} ` + numberToGematria(sectionNumber);
                      })()}`}
                    </h4>
                  </div>
                  <SectionStatistics
                    readingStats={selectedLevelThree.in_writing}
                    byHeartStats={selectedLevelThree.by_heart}
                    showAverageScore={true}
                  />
                </div>
                {!isTeacher && (
                  <Button 
                    className="circle" 
                    onClick={handleReadAgain} 
                    startIcon={<Autorenew />}
                    disabled={isLoadingReadAgain}
                  >
                    {t('readAgain')}
                  </Button>
                )}
              </Box>
            )}
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

export default observer(TanachAccordion);
